import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  ProgressBar,
  Row,
  Spinner,
  Table,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import PageHeader from "../../components/common/PageHeader";
import {
  createActivityLog,
  fetchCategories,
  importContact,
  updateActivityLog,
} from "../../service/apiService";

const REQUIRED_COLUMNS = ["NAME", "MOBILE NO", "PLACE"];

function normalizePhone(value) {
  return String(value || "").replace(/\D/g, "");
}

function buildRow(rawRow, index) {
  const name = String(rawRow?.["NAME"] ?? "").trim();
  const phone = normalizePhone(rawRow?.["MOBILE NO"] ?? "");
  const place = String(rawRow?.["PLACE"] ?? "").trim();
  const isValid = name !== "" && phone.length >= 10;

  return {
    id: `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
    sourceIndex: index + 1,
    name,
    phone,
    place,
    status: isValid ? "valid" : "invalid",
    error: isValid ? "" : "Name and a valid mobile number are required",
  };
}

function getStatusBadge(row) {
  switch (row.status) {
    case "success":
      return <Badge bg="success">Inserted</Badge>;
    case "skipped":
      return <Badge bg="info">Skipped</Badge>;
    case "failed":
      return <Badge bg="danger">Failed</Badge>;
    case "invalid":
      return <Badge bg="warning" text="dark">Invalid</Badge>;
    default:
      return <Badge bg="secondary">Ready</Badge>;
  }
}

export default function ImportContactsPage() {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentRow, setCurrentRow] = useState(null);
  const [importSummary, setImportSummary] = useState(null);
  const [skipDuplicates, setSkipDuplicates] = useState(false);
  const [showSheetModal, setShowSheetModal] = useState(false);
  const [sheetNames, setSheetNames] = useState([]);
  const [pendingWorkbook, setPendingWorkbook] = useState(null);
  const [selectedSheetName, setSelectedSheetName] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalPayload, setErrorModalPayload] = useState(null);
  const decisionResolverRef = useRef(null);

  const showToastMessage = (message, variant = "success") => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  useEffect(() => {
    let isMounted = true;

    const loadCategories = async () => {
      setLoadingCategories(true);
      try {
        const res = await fetchCategories();

        if (!isMounted) {
          return;
        }

        if (res?.status === "success") {
          setCategories(res.data || []);
        } else {
          setError("Failed to load categories");
        }
      } catch (loadError) {
        console.error("Category load error:", loadError);
        if (isMounted) {
          setError("Server not reachable");
        }
      } finally {
        if (isMounted) {
          setLoadingCategories(false);
        }
      }
    };

    loadCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (decisionResolverRef.current) {
        decisionResolverRef.current("stop");
        decisionResolverRef.current = null;
      }
    };
  }, []);

  const readyRows = useMemo(
    () => rows.filter((row) => row.status === "valid" || row.status === "failed"),
    [rows]
  );

  const failedRows = useMemo(
    () => rows.filter((row) => row.status === "failed"),
    [rows]
  );

  const successRows = useMemo(
    () => rows.filter((row) => row.status === "success"),
    [rows]
  );

  const skippedRows = useMemo(
    () => rows.filter((row) => row.status === "skipped"),
    [rows]
  );

  const invalidRows = useMemo(
    () => rows.filter((row) => row.status === "invalid"),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return rows;
    }

    return rows.filter((row) => {
      return (
        row.name?.toLowerCase().includes(query) ||
        row.phone?.includes(query) ||
        row.place?.toLowerCase().includes(query) ||
        row.status?.toLowerCase().includes(query)
      );
    });
  }, [rows, search]);

  const promptForErrorDecision = (payload) => {
    return new Promise((resolve) => {
      decisionResolverRef.current = resolve;
      setErrorModalPayload(payload);
      setShowErrorModal(true);
    });
  };

  const resolveErrorDecision = (decision) => {
    setShowErrorModal(false);
    setErrorModalPayload(null);

    if (decisionResolverRef.current) {
      decisionResolverRef.current(decision);
      decisionResolverRef.current = null;
    }
  };

  const resetPreviewState = () => {
    setImportSummary(null);
    setProgress(0);
    setCurrentRow(null);
    setError("");
  };

  const parseWorkbookSheet = (workbook, sheetName) => {
    try {
      const sheet = workbook.Sheets[sheetName];
      const parsedData = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      if (!parsedData.length) {
        setError("Selected sheet is empty");
        return;
      }

      const fileColumns = Object.keys(parsedData[0] || {});
      const missingColumns = REQUIRED_COLUMNS.filter(
        (column) => !fileColumns.includes(column)
      );

      if (missingColumns.length > 0) {
        setError(`Missing columns: ${missingColumns.join(", ")}`);
        return;
      }

      const formatted = parsedData.map((row, index) => buildRow(row, index));

      setRows(formatted);
      setSearch("");
      resetPreviewState();
      setShowSheetModal(false);
      setPendingWorkbook(null);
      setSelectedSheetName("");
    } catch (parseError) {
      console.error("Import parse error:", parseError);
      setError("Unable to read the selected sheet");
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (
      !file.name.toLowerCase().endsWith(".xlsx") &&
      !file.name.toLowerCase().endsWith(".csv")
    ) {
      setError("Only .xlsx and .csv files are allowed");
      return;
    }

    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const nextSheetNames = workbook.SheetNames || [];

        if (!nextSheetNames.length) {
          setError("No sheets found in the uploaded file");
          return;
        }

        if (nextSheetNames.length > 1) {
          setPendingWorkbook(workbook);
          setSheetNames(nextSheetNames);
          setSelectedSheetName(nextSheetNames[0]);
          setShowSheetModal(true);
          return;
        }

        parseWorkbookSheet(workbook, nextSheetNames[0]);
      } catch (parseError) {
        console.error("Import parse error:", parseError);
        setError("Unable to read the uploaded file");
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleSheetConfirm = () => {
    if (!pendingWorkbook || !selectedSheetName) {
      setError("Please select a sheet");
      return;
    }

    parseWorkbookSheet(pendingWorkbook, selectedSheetName);
  };

  const updateRow = (rowId, field, value) => {
    setRows((currentRows) =>
      currentRows.map((row) => {
        if (row.id !== rowId) {
          return row;
        }

        const nextRow = { ...row, [field]: value };
        const nextPhone = normalizePhone(nextRow.phone);
        const isValid = nextRow.name.trim() !== "" && nextPhone.length >= 10;

        nextRow.phone = nextPhone;

        if (nextRow.status !== "success") {
          nextRow.status = isValid ? (row.status === "failed" ? "failed" : "valid") : "invalid";
          nextRow.error = nextRow.status === "invalid"
            ? "Name and a valid mobile number are required"
            : "";
        }

        return nextRow;
      })
    );
  };

  const exportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      rows.map((row) => ({
        name: row.name,
        phone: row.phone,
        place: row.place,
        status: row.status,
        error: row.error,
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Contacts");
    XLSX.writeFile(workbook, "contacts_preview.xlsx");
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    autoTable(doc, {
      head: [["Name", "Phone", "Place", "Status"]],
      body: rows.map((row) => [
        row.name,
        row.phone,
        row.place || "-",
        row.status,
      ]),
    });
    doc.save("contacts_preview.pdf");
  };

  const handleSave = async () => {
    const contactsToProcess = rows.filter(
      (row) => row.status === "valid" || row.status === "failed"
    );
    const invalidCount = rows.filter((row) => row.status === "invalid").length;

    if (!contactsToProcess.length) {
      showToastMessage("No valid records to save", "warning");
      return;
    }

    if (!selectedCategory) {
      showToastMessage("Please select category", "warning");
      return;
    }

    setImporting(true);
    setImportSummary(null);
    setProgress(0);
    setCurrentRow(null);

    let insertedCount = 0;
    let failedCount = 0;
    let skippedCount = 0;
    let stopped = false;
    let activityLogId = null;

    const isDuplicateResponse = (response, message) => {
      const text = String(message || response?.message || "").toLowerCase();
      return (
        Boolean(response?.skipped_duplicate) ||
        response?.code === 409 ||
        text.includes("already exists") ||
        text.includes("duplicate")
      );
    };

    const buildSummary = (status, message) => ({
      status,
      imported: insertedCount,
      failed: failedCount,
      skipped: skippedCount,
      invalid: invalidCount,
      total: rows.length,
      processed: contactsToProcess.length,
      category_id: selectedCategory,
      message,
    });

    try {
      const startLog = await createActivityLog({
        action: "CONTACT_IMPORT_STARTED",
        message: "Contact import started",
        context: {
          category_id: selectedCategory,
          total_rows: rows.length,
          valid_rows: contactsToProcess.length,
          invalid_rows: invalidCount,
          skip_duplicates: skipDuplicates,
        },
        summary: buildSummary("started", "Import has started"),
      });

      if (startLog?.status === "success") {
        activityLogId = startLog?.data?.id || null;
      }
    } catch (logError) {
      console.warn("Unable to create import log:", logError);
    }

    for (let index = 0; index < contactsToProcess.length; index += 1) {
      const row = contactsToProcess[index];

      setCurrentRow({
        ...row,
        position: index + 1,
        total: contactsToProcess.length,
      });

      try {
        const response = await importContact({
          name: row.name,
          phone: row.phone,
          place: row.place,
          category_id: selectedCategory,
          tag: "Imported",
          is_opted_in: 1,
          skip_duplicates: skipDuplicates ? 1 : 0,
        });

        if (response?.status === "success" || (skipDuplicates && isDuplicateResponse(response))) {
          if (response?.skipped_duplicate || (skipDuplicates && isDuplicateResponse(response))) {
            skippedCount += 1;
            setRows((currentRows) =>
              currentRows.map((candidate) =>
                candidate.id === row.id
                  ? {
                      ...candidate,
                      status: "skipped",
                      error: response?.message || "Skipped duplicate number",
                    }
                  : candidate
              )
            );
          } else {
            insertedCount += 1;
            setRows((currentRows) =>
              currentRows.map((candidate) =>
                candidate.id === row.id
                  ? { ...candidate, status: "success", error: "" }
                  : candidate
              )
            );
          }
        } else if (skipDuplicates && isDuplicateResponse(response)) {
          skippedCount += 1;
          setRows((currentRows) =>
            currentRows.map((candidate) =>
              candidate.id === row.id
                ? {
                    ...candidate,
                    status: "skipped",
                    error: response?.message || "Skipped duplicate number",
                  }
                : candidate
            )
          );
        } else {
          failedCount += 1;
          const message = response?.message || "Failed to import contact";
          setRows((currentRows) =>
            currentRows.map((candidate) =>
              candidate.id === row.id
                ? { ...candidate, status: "failed", error: message }
                : candidate
            )
          );

          const decision = await promptForErrorDecision({
            row,
            message,
            response,
          });

          if (decision === "stop") {
            stopped = true;
            break;
          }
        }
      } catch (importError) {
        if (skipDuplicates && isDuplicateResponse(importError, importError?.message)) {
          skippedCount += 1;
          setRows((currentRows) =>
            currentRows.map((candidate) =>
              candidate.id === row.id
                ? {
                    ...candidate,
                    status: "skipped",
                    error: importError?.message || "Skipped duplicate number",
                  }
                : candidate
            )
          );
          setProgress(Math.round(((index + 1) / contactsToProcess.length) * 100));
          continue;
        }

        failedCount += 1;
        const message = importError?.message || "Server error";
        setRows((currentRows) =>
          currentRows.map((candidate) =>
            candidate.id === row.id
              ? { ...candidate, status: "failed", error: message }
              : candidate
          )
        );

        const decision = await promptForErrorDecision({
          row,
          message,
          response: null,
        });

        if (decision === "stop") {
          stopped = true;
          break;
        }
      }

      setProgress(Math.round(((index + 1) / contactsToProcess.length) * 100));
    }

    setImporting(false);
    setCurrentRow(null);
    setProgress((currentProgress) => (stopped ? currentProgress : 100));

    const summary = {
      inserted: insertedCount,
      failed: failedCount,
      skipped: skippedCount,
      invalid: invalidCount,
      total: contactsToProcess.length,
      overall_total: rows.length,
      stopped,
    };
    setImportSummary(summary);

    const finalStatus = stopped ? "CONTACT_IMPORT_INTERRUPTED" : "CONTACT_IMPORT_COMPLETED";
    const finalMessage = stopped
      ? "Import interrupted by user"
      : "Import completed successfully";

    if (activityLogId) {
      try {
        await updateActivityLog({
          id: activityLogId,
          action: finalStatus,
          message: finalMessage,
          context: {
            category_id: selectedCategory,
            skip_duplicates: skipDuplicates,
          },
          summary: {
            ...summary,
            status: stopped ? "interrupted" : "completed",
            message: finalMessage,
          },
        });
      } catch (logError) {
        console.warn("Unable to update import log:", logError);
      }
    }

    if (stopped) {
      showToastMessage(
        `Import stopped. Inserted ${insertedCount}, skipped ${skippedCount}, failed ${failedCount}.`,
        "warning"
      );
      return;
    }

    if (failedCount > 0) {
      showToastMessage(
        `Inserted ${insertedCount} contacts. ${skippedCount} skipped. ${failedCount} failed and can be edited below.`,
        "warning"
      );
      return;
    }

    showToastMessage(
      `Inserted ${insertedCount} contacts successfully${skippedCount > 0 ? `, skipped ${skippedCount} duplicates` : ""}`,
      "success"
    );
  };

  return (
    <Container fluid className="admin-page">
      <PageHeader
        title="Import Contacts"
        subtitle="Select a category, upload a file, and process rows with live progress"
      />

      <Card className="shadow-sm border-0 mb-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Label>Select Category</Form.Label>
              <Form.Select
                value={selectedCategory}
                onChange={(event) => setSelectedCategory(event.target.value)}
                disabled={loadingCategories}
              >
                <option value="">
                  {loadingCategories ? "Loading categories..." : "-- Select Category --"}
                </option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col md={4}>
              <Form.Label>Upload Excel (.xlsx, .csv)</Form.Label>
              <Form.Control type="file" accept=".xlsx,.csv" onChange={handleFileUpload} />
            </Col>

            <Col md={4} className="d-flex gap-2 justify-content-md-end">
              <Button
                variant="outline-secondary"
                onClick={exportExcel}
                disabled={!rows.length}
              >
                Export Excel
              </Button>
              <Button
                variant="outline-secondary"
                onClick={exportPDF}
                disabled={!rows.length}
              >
                Export PDF
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      {rows.length > 0 && (
        <Card className="shadow-sm border-0 mb-4">
          <Card.Header className="d-flex flex-column flex-md-row gap-3 justify-content-between align-items-md-center">
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <span className="fw-semibold">Preview</span>
              <Badge bg="secondary">{rows.length} total</Badge>
              <Badge bg="success">{successRows.length} inserted</Badge>
              <Badge bg="info">{skippedRows.length} skipped</Badge>
              <Badge bg="danger">{failedRows.length} failed</Badge>
              <Badge bg="warning" text="dark">
                {invalidRows.length} invalid
              </Badge>
            </div>

            <div className="d-flex align-items-center gap-2">
              <Button
                size="sm"
                variant="success"
                onClick={handleSave}
                disabled={importing}
              >
                {importing ? <Spinner size="sm" /> : failedRows.length ? "Retry Failed Rows" : "Save All"}
              </Button>
            </div>
          </Card.Header>

          <Card.Body>
            <Form.Check
              type="checkbox"
              className="mb-3"
              id="skip-duplicates"
              label="Skip duplicate numbers automatically"
              checked={skipDuplicates}
              onChange={(event) => setSkipDuplicates(event.target.checked)}
            />

            <Form.Control
              placeholder="Search name, phone, place, or status..."
              className="mb-3"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />

            {importing && (
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <small className="text-muted">
                    Processing {currentRow?.position || 0} / {currentRow?.total || readyRows.length}
                    {currentRow?.name ? `: ${currentRow.name}` : ""}
                  </small>
                  <small className="text-muted">{progress}%</small>
                </div>
                <ProgressBar
                  now={progress}
                  animated
                  striped
                  variant={progress === 100 ? "success" : "info"}
                />
              </div>
            )}

            <div style={{ maxHeight: 420, overflowY: "auto" }}>
              <Table striped hover size="sm" className="mb-0">
                <thead className="sticky-top bg-light">
                  <tr>
                    <th>Name</th>
                    <th>Mobile</th>
                    <th>Place</th>
                    <th>Status</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-4">
                        No rows match your search
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.name}</td>
                        <td>{row.phone}</td>
                        <td>{row.place || "-"}</td>
                        <td>{getStatusBadge(row)}</td>
                        <td className="text-danger">{row.error || "-"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {importSummary && (
        <Alert variant={importSummary.failed > 0 || importSummary.stopped ? "warning" : "success"}>
          Inserted {importSummary.inserted} of {importSummary.total} contacts.
          {importSummary.failed > 0 ? ` ${importSummary.failed} failed.` : " All rows were inserted."}
          {importSummary.stopped ? " Import was stopped early." : ""}
        </Alert>
      )}

      {failedRows.length > 0 && (
        <Card className="shadow-sm border-0 mt-4">
          <Card.Header className="d-flex flex-column flex-md-row gap-2 justify-content-between align-items-md-center">
            <div>
              <div className="fw-semibold">Failed Rows</div>
              <small className="text-muted">Edit the rows below and click Save All again to retry them.</small>
            </div>

            <Button
              size="sm"
              variant="primary"
              onClick={handleSave}
              disabled={importing || !failedRows.length}
            >
              Retry Failed Rows
            </Button>
          </Card.Header>

          <Card.Body>
            <div style={{ maxHeight: 420, overflowY: "auto" }}>
              <Table bordered responsive size="sm" className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th style={{ minWidth: 180 }}>Name</th>
                    <th style={{ minWidth: 160 }}>Mobile</th>
                    <th style={{ minWidth: 180 }}>Place</th>
                    <th style={{ minWidth: 120 }}>Status</th>
                    <th style={{ minWidth: 240 }}>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {failedRows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <Form.Control
                          value={row.name}
                          onChange={(event) =>
                            updateRow(row.id, "name", event.target.value)
                          }
                        />
                      </td>
                      <td>
                        <Form.Control
                          value={row.phone}
                          onChange={(event) =>
                            updateRow(row.id, "phone", event.target.value)
                          }
                        />
                      </td>
                      <td>
                        <Form.Control
                          value={row.place}
                          onChange={(event) =>
                            updateRow(row.id, "place", event.target.value)
                          }
                        />
                      </td>
                      <td>{getStatusBadge(row)}</td>
                      <td className="text-danger">{row.error || "Ready to retry"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      <Modal
        show={showSheetModal}
        onHide={() => {
          setShowSheetModal(false);
          setPendingWorkbook(null);
          setSelectedSheetName("");
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Sheet</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">This workbook has multiple sheets. Choose one to import.</p>
          <Form.Select
            value={selectedSheetName}
            onChange={(event) => setSelectedSheetName(event.target.value)}
          >
            {sheetNames.map((sheetName) => (
              <option key={sheetName} value={sheetName}>
                {sheetName}
              </option>
            ))}
          </Form.Select>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowSheetModal(false);
              setPendingWorkbook(null);
              setSelectedSheetName("");
            }}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSheetConfirm}>
            Import Sheet
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showErrorModal}
        onHide={() => resolveErrorDecision("stop")}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Contact import error</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            The current contact could not be inserted and will be skipped if you continue.
          </p>
          {errorModalPayload?.row && (
            <div className="small text-muted mb-3">
              <div>
                <strong>Name:</strong> {errorModalPayload.row.name || "-"}
              </div>
              <div>
                <strong>Phone:</strong> {errorModalPayload.row.phone || "-"}
              </div>
              <div>
                <strong>Place:</strong> {errorModalPayload.row.place || "-"}
              </div>
            </div>
          )}
          <Alert variant="danger" className="mb-0">
            {errorModalPayload?.message || "Unable to insert this contact"}
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => resolveErrorDecision("stop")}>
            Stop Import
          </Button>
          <Button variant="primary" onClick={() => resolveErrorDecision("continue")}>
            OK, continue
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="bottom-end" className="p-3">
        <Toast
          bg={toastVariant}
          onClose={() => setShowToast(false)}
          show={showToast}
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </Container>
  );
}
